import { getNodeDefinition } from './catalogue';
import type { FlowEdge, FlowGraph, FlowNode } from './graph';
import {
  DATA_TYPE_LABELS,
  type DataType,
  type InputPort,
  type NodeDefinition,
} from './types';

/** What an edge carries: a data type, possibly as a variant set. */
export interface EdgeType {
  base: DataType;
  variant: boolean;
}

/** A port's resolved type; undefined while it can't be known yet (nothing connected upstream). */
export type ResolvedType = EdgeType | 'trigger' | undefined;

export interface GraphIndex {
  nodes: Map<string, { node: FlowNode; definition: NodeDefinition | undefined }>;
  incoming: Map<string, FlowEdge[]>;
  outgoing: Map<string, FlowEdge[]>;
}

export function indexGraph(graph: FlowGraph): GraphIndex {
  const nodes: GraphIndex['nodes'] = new Map();
  const incoming = new Map<string, FlowEdge[]>();
  const outgoing = new Map<string, FlowEdge[]>();
  for (const node of graph.nodes) {
    nodes.set(node.id, {
      node,
      definition: getNodeDefinition(node.type, node.typeVersion),
    });
    incoming.set(node.id, []);
    outgoing.set(node.id, []);
  }
  for (const edge of graph.edges) {
    outgoing.get(edge.source.nodeId)?.push(edge);
    incoming.get(edge.target.nodeId)?.push(edge);
  }
  return { nodes, incoming, outgoing };
}

/** Kahn's order. Nodes on (or downstream of) a cycle never become ready and land in `cyclic`. */
export function topologicalOrder(index: GraphIndex): {
  order: string[];
  cyclic: Set<string>;
} {
  const remaining = new Map<string, number>();
  for (const [id, edges] of index.incoming) {
    remaining.set(id, edges.filter((edge) => index.nodes.has(edge.source.nodeId)).length);
  }
  const ready = [...remaining].filter(([, count]) => count === 0).map(([id]) => id);
  const order: string[] = [];
  while (ready.length > 0) {
    const id = ready.shift()!;
    order.push(id);
    for (const edge of index.outgoing.get(id) ?? []) {
      const left = (remaining.get(edge.target.nodeId) ?? 0) - 1;
      remaining.set(edge.target.nodeId, left);
      if (left === 0) ready.push(edge.target.nodeId);
    }
  }
  const placed = new Set(order);
  return {
    order,
    cyclic: new Set([...index.nodes.keys()].filter((id) => !placed.has(id))),
  };
}

export const portKey = (nodeId: string, port: string) => `${nodeId}:${port}`;

/**
 * Every output port's type, walking the graph in order: fixed types stay put,
 * `same` takes the type that came in, and the variant flag follows the port's
 * rule. Keyed by `portKey(nodeId, port)`.
 */
export function resolveTypes(
  graph: FlowGraph,
  index = indexGraph(graph),
): Map<string, ResolvedType> {
  const types = new Map<string, ResolvedType>();
  for (const id of topologicalOrder(index).order) {
    const entry = index.nodes.get(id);
    if (!entry?.definition) continue;
    const arriving = (index.incoming.get(id) ?? [])
      .map((edge) => types.get(portKey(edge.source.nodeId, edge.source.port)))
      .filter((type): type is EdgeType => type !== undefined && type !== 'trigger');
    const anyVariant = arriving.some((type) => type.variant);
    const bases = new Set(arriving.map((type) => type.base));

    for (const port of entry.definition.outputs) {
      let resolved: ResolvedType;
      if (port.type === 'trigger') resolved = 'trigger';
      else {
        const base =
          port.type === 'same'
            ? bases.size === 1
              ? [...bases][0]
              : undefined
            : port.type;
        const variant =
          port.variant === 'set' ? true : port.variant === 'clear' ? false : anyVariant;
        resolved = base ? { base, variant } : undefined;
      }
      types.set(portKey(id, port.id), resolved);
    }
  }
  return types;
}

/** A source post is a published reel, so it goes wherever a reel does. */
function assignable(from: DataType, to: readonly DataType[]): boolean {
  return to.includes(from) || (from === 'source' && to.includes('reel'));
}

/** Why `type` can't enter `port`, in words for the editor; undefined when it can. */
export function mismatch(
  port: InputPort,
  type: ResolvedType,
  definition: NodeDefinition,
): string | undefined {
  if (port.accepts === 'trigger') {
    return type === 'trigger'
      ? undefined
      : `${definition.label}'s trigger handle only takes a Schedule`;
  }
  if (type === 'trigger') return 'A Schedule can only start a starter';
  if (type === undefined) return undefined;
  if (port.accepts !== 'any' && !assignable(type.base, port.accepts)) {
    const wanted = port.accepts.map((base) => DATA_TYPE_LABELS[base]).join(' or ');
    return `${definition.label} takes ${wanted}, not ${DATA_TYPE_LABELS[type.base]}`;
  }
  if (port.variant === 'required' && !type.variant) {
    return `${definition.label} compares variants — put a Split test or Matrix upstream`;
  }
  return undefined;
}
